<template>
  <div>
    <div class="page-header mb-6">
      <SGPBotaoVoltarComponent />
      <div>
        <h1 class="page-title">{{ moduloSelecionado.nome }}</h1>
        <span class="page-subtitle">Gerenciamento de menus do módulo</span>
      </div>
    </div>

    <v-card class="mb-6" outlined>
      <v-card-title class="section-title d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-menu-open</v-icon>
          Menus disponíveis
        </div>
        <v-chip small color="primary" outlined>
          {{ selectedMenuIds.length }} selecionado(s)
        </v-chip>
      </v-card-title>
      <v-divider />

      <v-card-text class="pb-0">
        <p class="helper-text mb-4">
          Menus globais aparecem em qualquer módulo. Ao salvar uma seleção, os menus marcados passam a pertencer a este módulo e os desmarcados voltam a ser globais.
        </p>
      </v-card-text>

      <v-card-text class="pa-0">
        <v-simple-table v-if="menus.length">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left pl-4 checkbox-column">
                  <v-simple-checkbox
                    :value="todosSelecionados"
                    :indeterminate="algumSelecionado && !todosSelecionados"
                    color="primary"
                    @input="alternarTodos"
                  />
                </th>
                <th class="text-left">Nome</th>
                <th class="text-left">Rota</th>
                <th class="text-left">Menu pai</th>
                <th class="text-left pr-4">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="menu in menus" :key="menu.id" class="table-row">
                <td class="pl-4 checkbox-column">
                  <v-simple-checkbox
                    :value="estaSelecionado(menu.id)"
                    color="primary"
                    @input="alternarSelecao(menu.id, $event)"
                  />
                </td>
                <td>
                  <div class="font-weight-medium">{{ menu.menu }}</div>
                  <div class="text-caption text--secondary">ID {{ menu.id }}</div>
                </td>
                <td>
                  <span v-if="menu.route_link || menu.route_name">
                    {{ menu.route_link || menu.route_name }}
                  </span>
                  <span v-else class="text--secondary">Sem rota</span>
                </td>
                <td>
                  <span v-if="menu.acl_menu && menu.acl_menu.menu">{{ menu.acl_menu.menu }}</span>
                  <span v-else class="text--secondary">Menu raiz</span>
                </td>
                <td class="pr-4">
                  <v-chip
                    small
                    outlined
                    :color="corStatus(menu)"
                  >
                    {{ descricaoStatus(menu) }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div v-else class="pa-6 text-center text--secondary">
          <v-icon size="48" color="grey lighten-2">mdi-menu-off</v-icon>
          <p class="mt-2 mb-0">Nenhum menu disponível para este módulo.</p>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="justify-end pa-4">
        <v-btn
          color="primary"
          depressed
          :disabled="!houveAlteracoes"
          :loading="salvando"
          @click="salvarMenus"
        >
          <v-icon left>mdi-content-save</v-icon>
          Salvar associações
        </v-btn>
      </v-card-actions>
    </v-card>

    <SGPLoadingComponent v-if="loadingModulo" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ModuloMenuAssociacaoPage',

  data() {
    return {
      menus: [],
      selectedMenuIds: [],
      selectedMenuIdsInicial: [],
      salvando: false
    }
  },

  async created() {
    await this.carregarMenus()
  },

  computed: {
    ...mapGetters('ctrlModulos', ['loadingModulo', 'moduloSelecionado']),

    todosSelecionados() {
      return !!this.menus.length && this.selectedMenuIds.length === this.menus.length
    },

    algumSelecionado() {
      return this.selectedMenuIds.length > 0
    },

    houveAlteracoes() {
      return JSON.stringify(this.normalizarIds(this.selectedMenuIds)) !== JSON.stringify(this.normalizarIds(this.selectedMenuIdsInicial))
    }
  },

  methods: {
    ...mapActions('ctrlModulos', ['recuperarMenusDoModulo', 'sincronizarMenusDoModulo']),

    normalizarIds(ids = []) {
      return [...ids].map(id => Number(id)).sort((atual, proximo) => atual - proximo)
    },

    async carregarMenus() {
      const response = await this.recuperarMenusDoModulo(this.$route.params.modulo)
      const data = response?.data?.data || {}
      this.menus = data.menus || []
      this.selectedMenuIds = this.normalizarIds(data.selected_menu_ids || [])
      this.selectedMenuIdsInicial = [...this.selectedMenuIds]
    },

    estaSelecionado(menuId) {
      return this.selectedMenuIds.includes(Number(menuId))
    },

    alternarSelecao(menuId, selecionado) {
      const menuIdNormalizado = Number(menuId)

      if (selecionado && !this.estaSelecionado(menuIdNormalizado)) {
        this.selectedMenuIds = [...this.selectedMenuIds, menuIdNormalizado]
        return
      }

      if (!selecionado) {
        this.selectedMenuIds = this.selectedMenuIds.filter(id => id !== menuIdNormalizado)
      }
    },

    alternarTodos(selecionado) {
      this.selectedMenuIds = selecionado ? this.menus.map(menu => Number(menu.id)) : []
    },

    descricaoStatus(menu) {
      if (menu.is_global) return 'Global'
      if (menu.is_associado_ao_modulo) return 'Associado ao módulo'
      return 'Disponível para associação'
    },

    corStatus(menu) {
      if (menu.is_global) return 'success'
      if (menu.is_associado_ao_modulo) return 'primary'
      return 'grey'
    },

    async salvarMenus() {
      this.salvando = true

      try {
        await this.sincronizarMenusDoModulo({
          moduloId: this.$route.params.modulo,
          menus: this.normalizarIds(this.selectedMenuIds)
        })
        await this.carregarMenus()
      } finally {
        this.salvando = false
      }
    }
  }
}
</script>

<style scoped>
.page-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.page-subtitle {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.54);
}

.section-title {
  font-size: 1rem !important;
  font-weight: 600 !important;
  padding: 16px !important;
}

.helper-text {
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.875rem;
}

.checkbox-column {
  width: 56px;
}

.table-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>